import { useContext, useEffect, useState } from "react";
import { MdAdd, MdAddCircleOutline } from "react-icons/md";
import {
  ClassListType,
  ContactListType,
  StudentListType,
} from "utils/interfaces";
import TeacherCard from "./TeacherCard";
import SetPasswordPanel from "./SetPasswordPanel";
import AddTeacherPanel from "../add-teacher-pop-up";
import WarningPopUpMessage from "../common/WarningPopUpMessage";
import AnimatedToggle from "components/utils/ToggleButton";
import { createRecordRequest, updateRecordRequest } from "services/apiRequests";
import { formatUpdateClass } from "utils/formatUpdate";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";
import { AnimatePresence, motion } from "framer-motion";
import SavedSuccessfulPanel from "../saved-successful-pop-up";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import PopUpHeader from "../common/PopUpHeader";

interface CreateEditClassProps {
  selectedData?: ClassListType;
  handleClosePopUp: () => void;
  afterCreateEditClass?: () => void;
}

const CreateEditClass: React.FC<CreateEditClassProps> = ({
  selectedData,
  handleClosePopUp,
  afterCreateEditClass,
}) => {
  // States definition
  const [popUpState, setPopUpState] = useState<
    "main" | "add-teacher" | "set-password"
  >("main");
  const [classTitle, setClassTitle] = useState("");
  const [selectedTeachers, setSelectedTeachers] = useState<ContactListType[]>(
    []
  );
  const [leaderboardName, setLeaderboardName] = useState("");
  const [activateLeaderboard, setActivateLeaderboard] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [showSaveSuccessPopUp, setShowSaveSuccessPopUp] = useState(false);

  // Get the values from the context
  const { classesTerminology, dataRequirements } = usePlatform();
  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Expression to activate or deactivate create class button
   */
  const activateCreateButton =
    !!classTitle &&
    (!activateLeaderboard || (activateLeaderboard && !!leaderboardName));

  /**
   * If there is selected data, then prefill the defined fields
   */
  useEffect(() => {
    if (selectedData) {
      setClassTitle(selectedData.name);
      setSelectedTeachers(selectedData.teachers);
      setLeaderboardName(
        selectedData.leaderboard ? selectedData.leaderboard.name : ""
      );
      setActivateLeaderboard(
        selectedData.leaderboard ? selectedData.leaderboard.activate : false
      );
    }
  }, []);

  /**
   * Timer for the warning pop up message
   */
  useEffect(() => {
    if (showWarningMessage) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        if (setShowWarningMessage) setShowWarningMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showWarningMessage]);

  /**
   * Function that returns all the form data
   * @returns data
   */
  const provideData = () => {
    // Get the ids of the teachers selected
    const teacherIds = selectedTeachers.map(
      (teacher: ContactListType) => teacher._id
    );

    // Return all the gathered data in the form
    const dataToProvide: {
      name: string;
      teachers: string[];
      leaderboard: {
        name: string;
        activate: boolean;
      };
      organisations?: string[];
    } = {
      name: classTitle,
      teachers: teacherIds,
      leaderboard: {
        name: activateLeaderboard ? leaderboardName : "",
        activate: activateLeaderboard,
      },
    };

    // Formatting the data to be sent as a request
    const formattedData = formatUpdateClass(selectedData, dataToProvide);

    return formattedData;
  };

  /**
   * Handle press on add a teacher button
   */
  const handleAddATeacher = () => {
    setPopUpState("add-teacher");
  };

  /**
   * Function to execute once clicked "Create another"
   */
  const handleCreateAnother = () => {
    // Hide the success pop up
    setShowSaveSuccessPopUp(false);

    // And reset all of the data states
    setClassTitle("");
    setSelectedTeachers([]);
    setLeaderboardName("");
    setActivateLeaderboard(false);

    // And go to create-new state in the pop up
    setPopUpState("main");
  };

  /**
   * Function that wraps the function defined by create edit class
   * and show adds contextual functionality
   */
  const afterCreateFunction = () => {
    // Execute the after create edit class defined outside
    afterCreateEditClass && afterCreateEditClass();

    // And display the success message (if a new class has been created)
    if (!selectedData) setShowSaveSuccessPopUp(true);
    // But if editing, then close the pop up
    else handleClosePopUp();
  };

  /**
   * Function to handle creating a contact when the button is pressed
   * @returns
   */
  const handleCreateClass = async () => {
    // Check if the button is already available
    if (!activateCreateButton) return;

    // Get all the data from the form
    const data: any = provideData();

    // Request for creating new contact
    let response = { successful: false, message: "" };
    if (selectedData) {
      response = await updateRecordRequest(
        { _id: selectedData._id, toUpdate: data },
        "/api/classes"
      );
    } else {
      if (contextSchoolByRole && contextSchoolByRole._id) {
        data.organisations = [contextSchoolByRole._id];
      }
      response = await createRecordRequest(data, "/api/classes");
    }

    // Check if the response is successful
    if (!response.successful) {
      alert(response.message);
      return;
    }

    // Execute the defined actions after the creation
    afterCreateFunction();
  };

  /**
   * Function used when clicking the set password button
   */
  const handleSetPassword = () => {
    setPopUpState("set-password");
  };

  /**
   * Function used when clicking th download students button
   */
  const handleDownloadStudents = () => {
    // Double check that selectedData is defined
    if (!(selectedData && selectedData.students)) return;

    // Get the class name
    const className = selectedData.name;

    // Define the csv header of the table
    const header = "First name,Last name,Username\n";

    // Get the students from the selected class
    const students = selectedData.students;

    // Get the lines
    const content = students.map((student: StudentListType) => {
      return `${student.firstName},${student.lastName},${
        student.username || "-"
      }`;
    });
    // And transform every row into a string
    const contentString = content.join("\n");

    // Create a Blob with the CSV data
    const blob = new Blob([header + contentString], {
      type: "text/csv;charset=utf-8;",
    });

    // Create a link element
    const link = document.createElement("a");

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Set link attributes
    link.href = url;
    link.download = `${className}-student-list.csv`;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // And finally revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };

  /**
   * Function that deletes a teacher from the current class
   * @param contact
   */
  const deleteTeacher = (contact: ContactListType) => {
    // Deleting the contact with the current id
    const contactsFiltered = selectedTeachers.filter(
      (currentContact: ContactListType) => currentContact._id !== contact._id
    );
    setSelectedTeachers(contactsFiltered);
  };

  /**
   * Function executed when clicking blurred background
   */
  const handleClickBlackScreen = () => {
    setShowWarningMessage(true);
  };

  /**
   * Function executed when clicking the back arrow
   */
  const handlePressBack = () => {
    handleClosePopUp();
  };

  return (
    <>
      <motion.div
        key="create-edit-class-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClickBlackScreen}
      />
      {!showSaveSuccessPopUp && popUpState === "main" && (
        <motion.div
          key="create-edit-class-main"
          className="fixed w-[462px] h-fit left-0 right-0 ml-auto mr-auto p-6 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
        >
          <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
            <PopUpHeader
              title={
                selectedData
                  ? `Edit ${singlify(classesTerminology)}`
                  : `Create new ${singlify(classesTerminology)}`
              }
              handlePressBack={handlePressBack}
            />
            <div className="w-full h-[595px] flex-col justify-start items-start gap-8 inline-flex overflow-y-auto overflow-x-hidden pop-up-scrollbar">
              <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px] select-none">
                  Basic details
                </div>
                <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
                  <label
                    htmlFor="class-name"
                    className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
                  >
                    {singlify(classesTerminology)} Name*
                  </label>
                  <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                    <input
                      id="class-name"
                      type="text"
                      value={classTitle}
                      onChange={(event) => setClassTitle(event.target.value)}
                      className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                      placeholder={`Enter name of the ${singlify(
                        classesTerminology
                      ).toLowerCase()}`}
                    />
                  </div>
                </div>
              </div>
              <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px] select-none">
                  Teachers
                </div>
                {selectedTeachers.length === 0 ? (
                  <button
                    className="self-stretch px-2 py-3 rounded-lg border border-dashed border-neutral-300 hover:bg-neutral-25 ease-in-out duration-150 justify-center items-center gap-2 inline-flex cursor-pointer"
                    onClick={handleAddATeacher}
                  >
                    <div className="w-6 h-6 relative">
                      <MdAdd className="w-full h-full fill-neutral-500" />
                    </div>
                    <div className="text-neutral-500 text-base font-normal font-sans leading-[19px] select-none">
                      Add a Teacher
                    </div>
                  </button>
                ) : (
                  <div className="w-full h-fit flex-col justify-start items-start gap-4 inline-flex">
                    {selectedTeachers.map(
                      (teacher: ContactListType, index: number) => (
                        <TeacherCard
                          key={index}
                          teacher={teacher}
                          deleteAction={deleteTeacher}
                        />
                      )
                    )}
                    <button
                      className="group w-fit h-fit rounded justify-start items-center gap-2 flex cursor-pointer"
                      onClick={handleAddATeacher}
                    >
                      <div className="w-6 h-6 relative">
                        <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
                      </div>
                      <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                        Add additional teachers
                      </div>
                    </button>
                  </div>
                )}
              </div>
              {/* Leaderboard configuration */}
              <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px] select-none">
                  Leaderboard
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-start text-neutral-500 text-base font-normal font-sans leading-tight">
                    Include Leaderboard
                  </div>
                  <AnimatedToggle
                    state={activateLeaderboard}
                    handleClick={() =>
                      setActivateLeaderboard(!activateLeaderboard)
                    }
                    title="Include leaderboard toggle"
                  />
                </div>
                <AnimatePresence mode="wait">
                  {activateLeaderboard && (
                    <motion.div
                      key="leaderboard-options"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1, ease: "easeOut" }}
                      className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex"
                    >
                      <label
                        htmlFor="leaderboard-name"
                        className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
                      >
                        Leaderboard Name*
                      </label>
                      <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
                        <input
                          id="leaderboard-name"
                          type="text"
                          value={leaderboardName}
                          onChange={(event) =>
                            setLeaderboardName(event.target.value)
                          }
                          className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                          placeholder="Enter name of the leaderboard"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* Set password for students button */}
              {!!selectedData && !dataRequirements.includes("email") && (
                <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                  <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px]">
                    {singlify(classesTerminology)} password
                  </div>
                  <div
                    className="self-end w-full h-fit px-5 py-3 border border-secondary-600 hover:bg-neutral-50 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                    onClick={handleSetPassword}
                  >
                    <div className="text-secondary-600 text-lg font-semibold font-sans leading-snug">
                      Set password
                    </div>
                  </div>
                </div>
              )}
              {/* Download students button*/}
              {/* {!!selectedData && (
                  <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                    <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px]">
                      {studentsTerminology}
                    </div>
                    <div
                      className="self-end w-full h-fit px-5 py-3 border border-secondary-600 hover:bg-neutral-50 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                      onClick={handleDownloadStudents}
                    >
                      <div className="text-secondary-600 text-lg font-semibold font-sans leading-snug">
                        Download {studentsTerminology.toLowerCase()}
                      </div>
                    </div>
                  </div>
                )} */}
            </div>
            <button
              className={`self-end w-full h-fit px-5 py-3 rounded justify-center items-center gap-2 inline-flex ${
                activateCreateButton
                  ? "bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
                  : "bg-Subtle cursor-not-allowed"
              }`}
              onClick={handleCreateClass}
            >
              <div
                className={`text-lg font-semibold font-sans leading-snug select-none ${
                  activateCreateButton ? "text-white" : "text-neutral-400"
                }`}
              >
                {selectedData
                  ? `Save ${singlify(classesTerminology).toLowerCase()}`
                  : `Create ${singlify(classesTerminology).toLowerCase()}`}
              </div>
            </button>
            {/* Warning message shown when clicking in the blurred background */}
            <WarningPopUpMessage
              showWarningMessage={showWarningMessage}
              setShowWarningMessage={setShowWarningMessage}
            />
          </div>
        </motion.div>
      )}
      {/* Open the set password panel */}
      {!showSaveSuccessPopUp && popUpState === "set-password" && (
        <motion.div
          key="create-edit-class-set-password"
          className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
        >
          <SetPasswordPanel
            selectedData={selectedData}
            externalSetPopUpState={setPopUpState}
          />
        </motion.div>
      )}
      {/* Open the add teacher sub popup */}
      {!showSaveSuccessPopUp && popUpState === "add-teacher" && (
        <AddTeacherPanel
          key="create-edit-class-add-teacher"
          selectedTeachers={selectedTeachers}
          setSelectedTeachers={setSelectedTeachers}
          handleClosePopUp={() => setPopUpState("main")}
        />
      )}
      {showSaveSuccessPopUp && (
        <SavedSuccessfulPanel
          key="successful-panel"
          title={`${singlify(classesTerminology)} Created Successfully`}
          paragraphs={["The new class has been successfully created."]}
          createText="Create another"
          onCreateAnother={handleCreateAnother}
          onClose={handleClosePopUp}
          includeBackground={false}
        />
      )}
    </>
  );
};

export default CreateEditClass;
